import { pushNotificationAction } from 'actions/app-actions';
import calcPrice from 'common/utils/calc-price';
import formatCurrency from 'common/utils/format-currency';
import { NOTIFICATION_TYPE } from 'components/v2/notification/notification';
import { QUOTE_ERRORS_MAP } from 'constants/booking-constants';
import {
  COUNTRY_MAP,
  CURRENCY_CODE_TO_ZERO_QUOTE,
} from 'constants/country-constants';
import { isAreaZoningError, isAreaZoningSoftBlock } from 'helper/error-codes';
import {
  getCompanyID,
  getCompanyName,
  getGroupID,
  getGroupName,
  getUserCountryCode,
  getUserName,
} from 'helper/user';
import I18n from 'i18n-js';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _keyBy from 'lodash/keyBy';
import { EMPTY, of } from 'rxjs';

// eslint-disable-next-line import/prefer-default-export
export const isPromoAppliedInOrderQuote = (promo, quoteRequestID, quoteMap) => {
  const quote = _get(quoteMap, `[${quoteRequestID}].quotes[0]`, {});
  return !_isEmpty(promo) && _get(quote, 'isDiscountApplied', false);
};

export const enterpriseForQuoteParam = (
  userInfo,
  isTripDescriptionMandatory = true,
) => ({
  companyID: getCompanyID(userInfo),
  companyName: getCompanyName(userInfo),
  groupID: getGroupID(userInfo),
  groupName: getGroupName(userInfo),
  countryCode: getUserCountryCode(userInfo),
  isTripDescriptionMandatory,
  username: getUserName(userInfo) || 'UNKNOWN',
});

export const getQuotaMap = quotes =>
  !_isEmpty(quotes) ? _keyBy(quotes.deliveries, 'quoteRequestID') : {};

export const getTargetDeliveryQuotes = (fetchedQuotes, quoteRequestID) => {
  const targetDelivery = _get(fetchedQuotes, 'deliveries', []).filter(
    d => d.quoteRequestID === quoteRequestID,
  );

  return _get(targetDelivery, '[0].quotes') || [];
};

export const getQuoteAmountDefaultText = countryCode => {
  if (!countryCode) return '0.000';

  return _get(COUNTRY_MAP[countryCode], 'currency.defaultText', '0.000');
};

export const getQuoteAmountDefaultTextFromCurrencyCode = currencyCode =>
  _get(CURRENCY_CODE_TO_ZERO_QUOTE, currencyCode, '0.000');

/* Not checking existence of [quote.amount], as even for valid quote,
 * when amount is 0 (due to promo/ discount etc) BE does not return it in the response */
export const isValidQuote = quote => {
  if (_isEmpty(quote)) return false;

  const errors = _get(quote, 'errors', []);
  if (!_isEmpty(errors)) return false;

  // for failed quote, BE returns currency as: | currency: {code: "", symbol: "", exponent: 0} |
  const currency = _get(quote, 'currency', {});
  if (_isEmpty(currency)) return false;

  const symbol = _get(currency, 'symbol');
  if (_isEmpty(symbol)) return false;

  const code = _get(currency, 'code');
  if (_isEmpty(code)) return false;

  return true;
};

export const getFormattedAmount = (quote, countryCode) => {
  if (!isValidQuote(quote)) return getQuoteAmountDefaultText(countryCode);

  const { currency, amount } = quote;
  return formatCurrency({
    code: currency.code,
    symbol: currency.symbol,
    amount: calcPrice(amount, currency),
    hideSymbol: false,
  });
};

export const getFormattedTotalAmountFromQuotes = (quotes, countryCode) => {
  const defaultText = getQuoteAmountDefaultText(countryCode);

  if (_isEmpty(quotes)) return defaultText;

  const validQuotes = [];
  const totalAmount = quotes.reduce((acc, quote) => {
    if (!isValidQuote(quote)) return acc;

    // mark the available currency for generating final display price
    validQuotes.push(quote);
    const currentQuoteAmount = _get(quote, 'amount');
    const currentCurrency = _get(quote, 'currency');
    const currentFormattedAmount = calcPrice(
      currentQuoteAmount,
      currentCurrency,
    );
    return acc + currentFormattedAmount;
  }, 0);

  // No valid quote
  if (_isEmpty(validQuotes)) return defaultText;

  const { currency } = validQuotes[0];
  return formatCurrency({
    code: currency.code,
    symbol: currency.symbol,
    amount: totalAmount,
    hideSymbol: false,
  });
};

export const getQuoteErrNotiObservable = quoteToRender => {
  if (_isEmpty(quoteToRender.errors)) return EMPTY;

  const error = quoteToRender.errors[0];
  const errCode = _get(error, 'errorCode');

  // use BE provided title and message
  if (isAreaZoningError(errCode)) {
    const title = _get(error, 'title');
    const message = _get(error, 'message');
    return of(
      pushNotificationAction({
        message: title || I18n.t('v2.error.quote.getQuoteFailedError.message'),
        description: message || '',
        type: isAreaZoningSoftBlock(errCode)
          ? NOTIFICATION_TYPE.INFO
          : NOTIFICATION_TYPE.WARNING,
      }),
    );
  }

  // Existing logic for other error codes
  const errDescriptionKey = QUOTE_ERRORS_MAP[errCode];
  // fallback to BE returned error message if no matching error code
  const errDescription = _isEmpty(errDescriptionKey)
    ? _get(error, 'message')
    : I18n.t(errDescriptionKey);
  return of(
    pushNotificationAction({
      message: I18n.t('v2.error.quote.getQuoteFailedError.message'),
      description: errDescription,
      type: NOTIFICATION_TYPE.WARNING,
    }),
  );
};

export const getTotalAmountFromQuotes = quotes => {
  if (_isEmpty(quotes)) return 0;
  return quotes.reduce((acc, quote) => acc + getAmount(quote), 0);
};

export const getAmount = quote => {
  if (!isValidQuote(quote)) return 0;
  const { amount, currency } = quote;
  return calcPrice(amount, currency);
};

export const isSoftBlockError = error => {
  const errorCode = _get(error, 'errorCode');
  return isAreaZoningSoftBlock(errorCode);
};

export const filterHardBlockErrors = errors => {
  if (_isEmpty(errors)) return errors;
  return errors.filter(error => !isSoftBlockError(error));
};

export const hasOnlySoftBlockErrors = errors => {
  if (_isEmpty(errors)) return false;
  return errors.every(isSoftBlockError);
};
